import { FC, memo, useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Image, Skeleton, Text } from '@chakra-ui/react';
import { useCatalogsContext } from 'src/contexts/catalogs/CatalogsContext';
import { useChatData } from 'src/fusion-copilot/contexts/ChatDataContext';

interface CatalogSelectPreviewProps {
	catalogId: string;
	selected?: boolean;
	onClick?: () => void;
}

const loadedImagesCache = new Set<string>();

const CatalogSelectPreview: FC<CatalogSelectPreviewProps> = memo(
	({ catalogId, selected = false, onClick }) => {
		const { getCatalogById, isLoading } = useCatalogsContext();
		const { products } = useChatData();
		const apiCatalog = getCatalogById(catalogId);
		const [isDeleted, setIsDeleted] = useState(false);

		// If not found in API catalogs, look in chat products (scraped catalogs)
		const chatProduct = useMemo(() => {
			if (apiCatalog) return null;

			// Try to decode base64 catalogId (backend may send name encoded as base64)
			let decodedName = '';
			try {
				decodedName = atob(catalogId);
			} catch {
				// Not base64, use as-is
			}

			console.log('[CatalogSelectPreview] Looking for catalog:', {
				catalogId,
				decodedName,
				productsCount: products?.length || 0,
				productNames: products?.map((p) => p.name).slice(0, 5),
				productCatalogIds: products?.map((p) => p.catalogId).slice(0, 5),
			});

			const found = products.find(
				(p) =>
					p.catalogId === catalogId ||
					p.id === catalogId ||
					p.name === catalogId ||
					(decodedName && p.name === decodedName)
			);

			console.log('[CatalogSelectPreview] Found:', found?.name || 'null');
			return found;
		}, [apiCatalog, products, catalogId]);

		// Unified catalog object - prefer API catalog, fallback to chat product
		const catalog = useMemo(() => {
			if (apiCatalog) return apiCatalog;
			if (chatProduct) {
				return {
					id: chatProduct.id || chatProduct.catalogId || '',
					catalogId: chatProduct.catalogId || chatProduct.id || '',
					name: chatProduct.name,
					description: chatProduct.description,
					price: chatProduct.price,
					image: chatProduct.image,
					kind: chatProduct.kind || chatProduct.category || 'product',
				};
			}
			return null;
		}, [apiCatalog, chatProduct]);

		const imageToShow = catalog?.image || '/images/service-placeholder.png';
		const [isLoaded, setIsLoaded] = useState(
			imageToShow ? loadedImagesCache.has(imageToShow) : false,
		);

		useEffect(() => {
			if (isLoaded && imageToShow) {
				loadedImagesCache.add(imageToShow);
			}
		}, [isLoaded, imageToShow]);

		// Listen for catalog deletion events
		useEffect(() => {
			const handleCatalogDeleted = (event: CustomEvent) => {
				if (event.detail?.catalogId === catalogId) {
					setIsDeleted(true);
				}
			};

			window.addEventListener(
				'catalog-deleted',
				handleCatalogDeleted as EventListener,
			);

			return () => {
				window.removeEventListener(
					'catalog-deleted',
					handleCatalogDeleted as EventListener,
				);
			};
		}, [catalogId]);

		const handleClick = useCallback(() => {
			if (onClick) {
				onClick();
			}
		}, [onClick]);

		if (isLoading) {
			console.log('[CatalogSelectPreview] Showing loading state for:', catalogId);
			return (
				<Box
					borderRadius="lg"
					overflow="hidden"
					p={3}
					display="flex"
					flexDirection="row"
					alignItems="center"
					gap={3}
					bg="white"
					border="1px solid"
					borderColor="gray.200"
					minH="70px"
					minW="min(400px, 100%)"
					maxW="100%"
				>
					<Skeleton h="50px" w="50px" borderRadius="md" />
					<Skeleton h="16px" w="120px" />
				</Box>
			);
		}

		if (!catalog || isDeleted) {
			console.log('[CatalogSelectPreview] Not rendering - catalog:', !!catalog, 'isDeleted:', isDeleted, 'catalogId:', catalogId);
			return null;
		}

		console.log('[CatalogSelectPreview] Rendering catalog:', catalog.name, 'catalogId:', catalogId);

		return (
			<Box
				borderRadius="lg"
				overflow="hidden"
				p={3}
				display="flex"
				flexDirection="row"
				alignItems="center"
				gap={3}
				bg="white"
				border="2px solid"
				borderColor={selected ? '#F7480B' : 'gray.200'}
				boxShadow={selected ? 'md' : 'sm'}
				transition="all 0.2s ease-in-out"
				cursor="pointer"
				_hover={{
					boxShadow: 'md',
				}}
				onClick={handleClick}
				minH="70px"
				minW="min(400px, 100%)"
				maxW="100%"
				flex="1"
			>
				<Box
					position="relative"
					w="50px"
					h="50px"
					borderRadius="md"
					overflow="hidden"
					boxShadow="sm"
					flexShrink={0}
				>
					{!isLoaded && <Skeleton h="50px" w="50px" borderRadius="md" />}
					<Image
						src={imageToShow}
						alt={catalog.name}
						onError={(e) => {
							// Only log error if it's not the placeholder image
							if (imageToShow !== '/images/service-placeholder.png') {
								console.error('Error loading image:', imageToShow);
							}
							setIsLoaded(true);
							// Set a fallback background color when image fails
							e.currentTarget.style.display = 'none';
						}}
						onLoad={() => setIsLoaded(true)}
						display={isLoaded ? 'block' : 'none'}
						objectFit="cover"
						w="50px"
						h="50px"
					/>
				</Box>

				<Box
					flex="1"
					display="flex"
					flexDirection="column"
					justifyContent="center"
					minH="50px"
				>
					<Text
						fontSize="md"
						fontWeight="medium"
						color="gray.800"
						noOfLines={2}
						lineHeight="1.3"
					>
						{catalog.name}
					</Text>
				</Box>
			</Box>
		);
	},
);

CatalogSelectPreview.displayName = 'CatalogSelectPreview';

export default CatalogSelectPreview;
